import { numberWithCommas } from '../common/util';
import { UIButton } from '../common/ui/uiButton';
import { UICanvas } from '../common/ui/uiCanvas';
import { UIGrid } from '../common/ui/uiGrid';
import { UIImage } from '../common/ui/uiImage';
import { UIPanel } from '../common/ui/uiPanel';
import { UIPanelButton } from '../common/ui/uiPanelButton';
import { UIScreen } from '../common/ui/uiScreen';
import { UIText } from '../common/ui/uiText';
import { CloudsPanel } from './cloudsPanel';
import { CountdownPanel } from './countdownPanel';
import { Game } from './game';
import { GameStyle } from './gameStyle';
import { MolePanel } from './molePanel';
import { TimePanel } from './timePanel';
import { DistractorPanel } from './distractorPanel';
import { PowerupPanel } from './powerupPanel';
import { LevelPanel } from './levelPanel';
import { Server } from '../../server/server';
import { AppConfig } from '../../app/AppConfig';

/**
 * A small amount of time will be added to the clock when reached higher level.
 */
const EXTEND_TIME = 5000; // 5s

export class GameScreen extends UIScreen {
  protected clouds: CloudsPanel;
  protected moles: MolePanel[];
  protected moleSpawnTime: number;
  protected distractor: DistractorPanel;
  protected distractorSpawnTime: number;
  protected toasts: any[];
  protected grid: UIGrid;
  protected scoreText: UIText;
  protected countdownPanel: CountdownPanel;
  protected started: boolean;
  protected timePanel: TimePanel;
  protected playWithFriendsButton: UIButton;

  // For powerups functionality
  protected powerup: PowerupPanel;
  protected multiplierActive = -1;
  protected slowMotionActive = -1;
  protected shieldsActive = -1;

  // For Level Progression
  protected levelToastText: UIText;
  protected levelPanel: LevelPanel;

  protected playPlaceButton: UIPanelButton;

  public build() {
    this.mouseEvents = true;

    let background = new UIPanel({
      fitParent: {left: 0, right: 0, top: 0, bottom: 0},
      gradient: {
        startColor: '#b9967b',
        endColor: '#512701',
        type: 'vertical'
      },
      parent : this
    });

    this.clouds = new CloudsPanel({
      fitParent: {left: 0, right: 0, top: -1, bottom: -1},
      size: {x: 0, y: 180},
      parent : this
    });

    this.distractor = new DistractorPanel({
      fitParent: {left: 0, right: 0, top: -1, bottom: -1},
      position: {x: 0, y: 50},
      size: {x: 0, y: 120},
      callback: (score:number, panel: DistractorPanel, x: number, y: number) => {
        this.distractorTapped(score, x, y);
      },
      parent : this
    });

    let topbar = new UIPanel({
      fitParent: {left: 0, right: 0, top: -1, bottom: -1},
      size: {x: 0, y: 50},
      color: '#00000080',
      parent : this
    });

    let scorePanel = new UIPanel({
      anchor: {x: 0.5, y: 0.5},
      pivot: {x: 0.5, y: 0.5},
      size: {x: 100, y: 50},
      // radius: 10,
      // color: '#00000080',
      parent : topbar
    });

    let scoreLabel = new UIText({
      text : 'SCORE',
      fontFamily: GameStyle.fonts.small.family,
      fontSize: GameStyle.fonts.small.size,
      anchor: {x: 0.5, y: 0},
      pivot: {x: 0.5, y: 0},
      position: {x: 0, y: 2},
      color: '#ffffff80',
      parent : scorePanel
    });

    this.scoreText = new UIText({
      text : '',
      fontFamily: GameStyle.fonts.large.family,
      fontSize: GameStyle.fonts.large.size,
      fontWeight: '700',
      anchor: {x: 0.5, y: 0.5},
      pivot: {x: 0.5, y: 0.5},
      position: {x: 0, y: 6},
      color: '#ffffff',
      parent : scorePanel
    });

    this.timePanel = new TimePanel({
      anchor: {x: 1, y: 0},
      pivot: {x: 1, y: 0},
      size: {x: 40, y: 40},
      position: {x: -10, y: 5},
      // radius: 10,
      // color: '#00000080',
      callback: ()=>{
        this.onGameOver();
      },
      parent : topbar
    });

    this.levelPanel = new LevelPanel({
      size: {x: 40, y: 40},
      position: {x: 60, y: 5},
      parent : topbar
    });

    let bottombar = new UIPanel({
      fitParent: {left: 0, right: 0, top: -1, bottom: -1},
      size: {x: 0, y: 50},
      anchor: {x: 0, y: 1},
      pivot: {x: 0, y: 1},
      color: '#00000080',
      parent : this
    });

    this.powerup = new PowerupPanel({
      callback: (plusScore: number, slowMotion: number, shields: number) => {
        this.multiplierActive = plusScore;
        this.slowMotionActive = slowMotion;
        this.shieldsActive    = shields;
      },
      parent : bottombar
    });

    this.grid = new UIGrid({
      columns: 3,
      rows: 4,
      spacing: {x: 10, y: 0},
      size: {x: 20 + (100 * 3), y: 0 + (100 * 4)},
      anchor : {x : 0.5, y : 0},
      pivot : {x : 0.5, y : 0},
      position : {x : 0, y : 150},
      // color: '#ffffff80',
      parent : this
    });

    this.toasts = [];
    this.moles = [];
    for(let i = 0; i < 12; i++) {
      let mole = new MolePanel({
        name: i.toString(),
        position: {x: 20, y: 200},
        callback: (score:number, panel:MolePanel, isMissed:boolean)=>{
          this.moleTapped(score, panel, isMissed);
        },
        parent : this.grid
      });
      this.moles.push(mole);
    }

    this.levelToastText = new UIText({
      text : '',
      fontFamily : 'verdana',
      fontSize : 32,
      fontWeight: '800',
      anchor: {x: 0.5, y: 0},
      pivot: {x: 0.5, y: 0.5},
      position: {x: 0, y: 150},
      strokeLineWidth: 2,
      strokeColor: '#444000',
      color: '#FFF200',
      parent : this
    })

    this.countdownPanel = new CountdownPanel({
      anchor: {x: 0.5, y: 0},
      pivot: {x: 0.5, y: 0},
      fitParent: {left: 0, right: 0, top: -1, bottom: -1},
      size: {x: 0, y: 100},
      position: {x: 0, y: 200},
      parent : this
    });

    let finishButton = new UIButton({
      elements: {
        normal: new UIImage({
          url : require('./assets/button-menu.png')
        })
      },
      anchor: {x: 1, y: 0},
      pivot: {x: 1, y: 0},
      size: {x: 40, y: 40},
      position: {x: -10, y: 5},
      callback : (btn:UIPanelButton) => {
        this.onMenu();
      },
      parent : bottombar
    });

    this.playPlaceButton = new UIPanelButton({
      size: {x: 40, y: 40},
      position: {x: 10, y: 5},
      callback : () => {
        this.onPPMenu();
      },
      // panelColors: GameStyle.dialog.button.panelColors,
      panelColors: {normal: ''},
      radius: 23,
      borderWidth: 4,
      borderColor: '#00000040',
      parent : topbar
    });

    let playPlaceIcon = new UIImage({
      url : '/playplace.png',
      parent : this.playPlaceButton
    });
  }

  protected onPPMenu() {
    this.pause();

    Game.instance.playPlaceMenu.showModal((action:string)=>{
      if (action) {
        Game.instance.askQuestion('Quit the game and\nreturn to Play Place?', (response:string)=>{
          if(response == 'Yes') {
            let menu = AppConfig.menu;

            if(action == menu[0].text)
              window.open(menu[0].to, '_self');
            else if(action == menu[1].text)
              window.open(menu[1].to, '_self');
            else if(action == menu[2].text)
              window.open(menu[2].to, '_self');
            else if(action == menu[3].text)
              window.open(menu[3].to, '_self');
            else if(action == menu[5].text)
              window.open(menu[5].to, '_self');
            else if(action == menu[6].text)
              window.open(menu[6].to, '_self');
          }
          else
            this.resume();
        });
      }
      else
        this.resume();
    });
  }

  private onEventUpdated(data: any) {
    if(data.action == 'connect' || data.action == 'disconnect' || data.action == 'presence')
      return;

    let dot = new UIPanel({
      size: {x: 10, y: 10},
      position: {x: 32, y: 3},
      radius: 5,
      color: 'red',
      parent : this.playPlaceButton
    });
  }

  public onWake() {
    let gridY = (UICanvas.instance.height - 400) * 0.60;
    this.grid.position.y = gridY;
    this.clouds.size.y = gridY + 30;
    this.distractor.size.y = gridY + 30 - this.distractor.position.y;
    this.levelToastText.visible = false;
    this.levelToastText.position.y = this.distractor.position.y + (this.distractor.size.y/2);

    this.clouds.start();
    this.timePanel.setDuration(60);
    this.startGame();

    this.onEventUpdated = this.onEventUpdated.bind(this);
    Server.messages.addEventListener('chat-started', this.onEventUpdated);
    Server.messages.addEventListener('chat-updated', this.onEventUpdated);
    Server.user.addEventListener('friend-updated', this.onEventUpdated);
  }

  public update() {
    super.update();

    let now = performance.now();
    
    if(this.started && now >= this.moleSpawnTime && !this.levelToastText.visible) {
      let moles = [];
      for(let i = 0; i < this.moles.length; i++)
        if(this.moles[i].hidden)
          moles.push(this.moles[i]);

      if(moles.length == 0)
        return;

      let i = Math.floor(Math.random() * moles.length);
      moles[i].show(this.slowMotionActive, this.getMoleSpawnRate());

      this.scheduleNextMole();
    }

    for(let i = 0; i < this.toasts.length; i++) {
      let t = this.toasts[i];
      t.text.position.x += (t.vx * this.deltaTime);
      t.text.position.y += (t.vy * this.deltaTime);
      t.vy += (0.0005 * this.deltaTime);
      if(t.text.getAlpha() == 0) {
        t.text.parent.removeChild(t.text);
        this.toasts.splice(i, 1);
        i--;
      }
    }

    // show distractor
    if (this.started && this.distractor.hidden && now >= this.distractorSpawnTime && !this.levelToastText.visible) {
      this.distractor.show();
      this.scheduleNextDistractor();
    }
  }

  protected resetGame() {
    Game.instance.score = 0;
    Game.instance.level = 1;
    this.scoreText.text = '0';
    this.started = false;
    this.timePanel.reset();
    this.powerup.reset();
    this.distractor.reset();
    this.resetLevel();
  }

  public startGame() {
    this.resetGame();

    Game.instance.registerGame();

    Game.instance.pupSlotsDialog.showModal(async () => {
      this.powerup.buildPowerups();
      this.toastLevelUp();
      
      setTimeout(() => {
        this.started = true;
        this.scheduleNextMole();
        this.scheduleNextDistractor();
        this.timePanel.start();
      }, 2000);
    });
  }

  public finishGame() {
    UICanvas.instance.hideAllDialogs();

    this.timePanel.stop();
    this.started = false;

    for(let i = 0; i < this.moles.length; i++)
      this.moles[i].pause();

    this.waitFor(1000, ()=>{
      console.log('GAME OVER');
      // Game.instance.showGameOver();
    });
  }

  public getMoleSpawnRate() {
    return 600 - ((Game.instance.level-1) * 35);
  }

  protected scheduleNextMole() {
    this.moleSpawnTime = performance.now() + this.getMoleSpawnRate();
  }

  protected scheduleNextDistractor() {
    this.distractorSpawnTime = performance.now() + (5000 + (Math.random() * 3000));
  }

  public score(score:number, mole:MolePanel, isMissed: boolean, sx:number = 0, sy:number = 0, eject:boolean=true) {
    if(!this.started)
      return;

    Game.instance.score += score;

    this.scoreText.text = numberWithCommas(Game.instance.score);

    let scoreStr = score.toString();
    if (scoreStr.substring(0, 1) != '-')
      scoreStr = '+' + scoreStr;

    let toast = {
      text: new UIText({
        text : scoreStr,
        fontFamily : 'arial',
        fontSize : 24,
        fontWeight: '700',
        anchor: {x: mole ? 0.5 : 0, y: mole ? 0.5 : 0},
        pivot: {x: 0.5, y: 0.5},
        position : {x : sx, y : sy},
        strokeLineWidth: 3,
        strokeColor: score > 0 ? '#00aa00' : '#aa0000',
        color: score > 0 ? '#004400' : '#440000',
        parent : mole ? mole : this
      }),
      vx: eject ? (0.005 + (Math.random() * 0.01)) * (Math.random() > 0.5 ? 1 : -1) : 0,
      // vx: 0,
      vy: eject ? -0.35 : -0.1
    }

    setTimeout(() => {
      toast.text.fadeOut(1000);
    }, 500);

    this.toasts.push(toast);
  }

  private toastBlocked(mole: MolePanel, sx: number = 0, sy: number = 0) {
    let toast = {
      text: new UIText({
        text : 'BLOCKED',
        fontFamily : 'arial',
        fontSize : 20,
        fontWeight: '700',
        anchor: {x: mole ? 0.5 : 0, y: mole ? 0.5 : 0},
        pivot: {x: 0.5, y: 0.5},
        position : {x : sx, y : sy},
        strokeLineWidth: 2,
        strokeColor: 'black',
        color: '#DB00C2',
        parent : mole ? mole : this
      }),

      vx: (0.005 + (Math.random() * 0.01)) * (Math.random() > 0.5 ? 1 : -1),
      vy: -0.35
    }

    setTimeout(() => {
      toast.text.fadeOut(1000);
    }, 500);

    this.toasts.push(toast);
  }

  public onMouseDown(x:number, y: number): void {
    let child = this.findChildFromPoint(x, y);
    if (child == this) {
      this.groundTapped(x - this.getScreenX(), y - this.getScreenY());
    }
  }

  public pause() {
    this.started = false;
    this.timePanel.pause();
    this.powerup.pause();
    this.distractor.pause();
    for(let i = 0; i < this.moles.length; i++)
      this.moles[i].pause();
  }

  public resume() {
    this.timePanel.resume();
    this.powerup.resume();
    this.started = true;
    this.distractor.resume();

    if(this.distractor.hidden)
      this.scheduleNextDistractor();
  }

  protected onGameOver() {
    if(this.levelToastText.visible) {
      setTimeout(() => {
        this.onGameOver();
      }, 100);
      return;
    }

    this.hideAllMoles();
    if(!this.distractor.hidden)
      this.distractor.hide();

    this.started = false;

    let y = this.levelToastText.position.y;

    this.levelToastText.text = 'GAME OVER';
    this.levelToastText.visible = true;
    this.levelToastText.slide(0 + (UICanvas.instance.width/2) + this.levelToastText.width, y, 0, y, 250);

    setTimeout(() => {
      this.levelToastText.slide(0, y, 0 - (UICanvas.instance.width/2) - this.levelToastText.width, y, 250, ()=>{
        // this.levelToastText.visible = false;
        Game.instance.gameOverDialog.showModal();
      });
    }, 2500);
  }

  protected onMenu() {
    if(!this.started || this.levelToastText.visible)
      return;

    this.pause();

    Game.instance.pauseDialog.showModal((action:string)=>{
      if(action == 'restart') {
        Game.instance.askQuestion('Start a new game?', (response:string)=>{
          if(response == 'Yes')
            this.startGame();
          else
            this.resume();
        });
      }
      else if(action == 'quit') {
        Game.instance.askQuestion('Quit the game?', (response:string)=>{
          if(response == 'Yes')
            Game.instance.quit();
          else
            this.resume();
        });
      }
      else
        this.resume();
    });
  }

  private resetLevel() {
    Game.instance.level = 1;
    Game.instance.combo = 0;
    Game.instance.maxCombo = 16; 
  }

  private levelScore(score: number, isMissed: boolean) {
    // increase 25% score per level (from level 2)
    score = score * (1 + (Game.instance.level - 1) * 0.25);
    score = Math.round(score);
    return score;
  }

  private showScore(score: number, panel: MolePanel, isMissed: boolean) {
    if(score == 0)
      return;

    this.score(score, panel, isMissed);
    
    if (this.multiplierActive != -1 && score > 0) {
      setTimeout(()=>{
        this.score(score, panel, isMissed);
      }, 100);
    }
  }

  private processCombo(score:number, panel:MolePanel, sx: number, sy: number) {
    if(score <= 0) {
      if(Game.instance.combo > 0) {
        if (this.shieldsActive != -1) {
          this.toastBlocked(panel, sx, sy);
        }
        else {
          Game.instance.combo = Math.max(0, Game.instance.combo - 4);
          this.toastStreakBroken(panel, sx, sy);
        }
      } 
    }
    else {
      Game.instance.combo++;
      if (Game.instance.combo === Game.instance.maxCombo) {
        Game.instance.level++;
        Game.instance.combo = 0;
        Game.instance.maxCombo += 1;
        this.timePanel.extendRemainingTime(EXTEND_TIME);
        this.toastLevelUp();
        this.hideAllMoles();
        if(!this.distractor.hidden)
          this.distractor.hide();
        this.scheduleNextDistractor();
      }
    }
  }

  private groundTapped(x: number, y: number) {
    if(this.levelToastText.visible || !this.started)
      return;

    this.processCombo(0, null, x, y);
  }

  private distractorTapped(score:number, x: number, y: number) {
    if(this.levelToastText.visible || !this.started)
      return;

    let sx = x - this.getScreenX();
    let sy = y - this.getScreenY();
    this.processCombo(score, null, sx, sy);

    if(score > 0)
      this.score(score, null, false, sx, sy, false);
  }
  
  private moleTapped(score:number, panel:MolePanel, isMissed:boolean) {
    if(this.levelToastText.visible || !this.started)
      return;

    this.processCombo(score, panel, 0, 0);

    if(score > 0) {
      score = this.levelScore(score, isMissed);
      this.showScore(score, panel, isMissed);
    }
  }

  private toastLevelUp() {
    let y = this.levelToastText.position.y;

    this.levelToastText.text = 'LEVEL ' + Game.instance.level;
    this.levelToastText.visible = true;
    this.levelToastText.slide(0 + (UICanvas.instance.width/2) + this.levelToastText.width, y, 0, y, 250);

    setTimeout(() => {
      this.levelToastText.slide(0, y, 0 - (UICanvas.instance.width/2) - this.levelToastText.width, y, 250, ()=>{
        this.levelToastText.visible = false;
      });
    }, 1500);
  }

  private toastStreakBroken(mole:MolePanel, sx:number=0, sy:number=0) {
    let toast = {
      text: new UIText({
        text : '-STREAK',
        fontFamily : 'arial',
        fontSize : 20,
        fontWeight: '700',
        anchor: {x: mole ? 0.5 : 0, y: mole ? 0.5 : 0},
        pivot: {x: 0.5, y: 0.5},
        position : {x : mole ? 0 : sx, y : mole ? 0 : sy},
        strokeLineWidth: 2,
        strokeColor: '#000000',
        color: '#FF0000',
        parent : mole ? mole : this
      }),
      vx: (0.005 + (Math.random() * 0.01)) * (Math.random() > 0.5 ? 1 : -1),
      vy: -0.35
    }

    setTimeout(() => {
      toast.text.fadeOut(1000);
    }, 500);

    this.toasts.push(toast);
  }

  private hideAllMoles() {
    for(let i = 0; i < this.moles.length; i++) {
      if(!this.moles[i].hidden)
        this.moles[i].hide(true);
    } 
  }
}